// Thin fetch wrapper that:
//   - prepends VITE_API_BASE (empty in dev → Vite proxy handles /command-center)
//   - injects Authorization: Bearer <keycloak-access-token> via the getter wired by AuthContext
//   - throws ApiError on non-2xx; surfaces 401 so callers can react
//
// WaffySSO: the bearer token is now a Keycloak (waffy-staff) access token, NOT the
// legacy command-center-api self-issued HS256 JWT. command-center-api MUST be updated
// to validate it against the realm JWKS — see MIGRATION.md "API coupling".

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
// Hard ceiling on any single request so a slow/hung upstream can never leave the UI
// spinning forever — it becomes a clean error state instead. The API's own upstream
// adapters time out at ~5s and degrade, so 20s is comfortably above a legit slow call
// (e.g. the live-signals snapshot fanning out) while still bounding true hangs.
const TIMEOUT_MS = Number(import.meta.env.VITE_API_TIMEOUT_MS ?? 20000);

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// AuthContext injects a getter that returns the current (non-expired) access token.
// Kept as a setter to avoid a circular import between client.ts and the OIDC layer.
type TokenGetter = () => Promise<string | null>;
let accessTokenGetter: TokenGetter = async () => null;
export function setAccessTokenGetter(getter: TokenGetter) {
  accessTokenGetter = getter;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
  form?: Record<string, string>;
  /** Skip the Authorization header entirely. */
  noAuth?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!opts.noAuth) {
    const token = await accessTokenGetter();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  let body: BodyInit | null = null;
  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(opts.json);
  } else if (opts.form) {
    headers.set("Content-Type", "application/x-www-form-urlencoded");
    body = new URLSearchParams(opts.form).toString();
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  let r: Response;
  try {
    r = await fetch(url, { ...opts, headers, body, signal: ctrl.signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError(408, `request timed out after ${TIMEOUT_MS}ms: ${path}`);
    }
    // Network error (API down, CORS, DNS) → surface as a non-2xx-style ApiError so
    // callers render an error state instead of hanging on a rejected promise.
    throw new ApiError(0, (err as Error).message || "network error");
  } finally {
    clearTimeout(timer);
  }

  if (r.status === 401) {
    // Token rejected/expired. oidc-client-ts handles renewal; if it can't, the
    // AuthProvider's silent-renew-error / token-expired listeners flip to anonymous.
    throw new ApiError(401, "unauthorized", await safeJson(r));
  }
  if (!r.ok) throw new ApiError(r.status, `HTTP ${r.status}`, await safeJson(r));

  // 204 No Content
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

async function safeJson(r: Response): Promise<unknown> {
  try { return await r.json(); } catch { return undefined; }
}

export const api = {
  get:  <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, opts: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...opts, method: "POST" }),
};

// NOTE: fetchStatic() was removed when the registry artifacts moved behind the API
// (GET /command-center/registry/*, Keycloak-authed). DataContext now uses api.get().
